import { useRef, useState } from "react";
import { Clock, Signal, Play, CalendarCheck, ListChecks, Gift } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/landing/Reveal";
import { CHECKOUT_URL, IMAGES } from "@/lib/site";

const APP_POINTS = [
  { icon: CalendarCheck, text: "A day-by-day 14-Day Transformation program — no thinking required" },
  { icon: ListChecks, text: "Sets, reps, and rest timers guided on screen while you follow along" },
  { icon: Gift, text: "Free warm-up and mobility sessions included with every plan" },
];

const VideoCard = () => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const start = () => {
    setPlaying(true);
    videoRef.current?.play();
  };
  return (
    <div className="bg-slate-950 rounded-[2rem] border border-slate-800 overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.3)] transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 hover:shadow-[0_24px_50px_rgb(0,0,0,0.45)]">
      <div className="relative overflow-hidden">
        <video ref={videoRef} data-testid="workout-video" src={IMAGES.video} poster={IMAGES.videoPoster} controls={playing} playsInline preload="none"
          className="w-full aspect-[4/5] sm:aspect-[3/3.1] object-cover object-top" />
        {!playing && (
          <button type="button" data-testid="workout-video-preview" onClick={start} aria-label="Play sample workout video"
            className="group absolute inset-0 w-full h-full cursor-pointer focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:outline-none focus-visible:ring-inset">
            <span className="absolute inset-0 bg-slate-900/10 group-hover:bg-slate-900/25 transition-colors duration-300" aria-hidden="true" />
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="relative flex items-center justify-center w-20 h-20 rounded-full bg-blue-600 text-white shadow-[0_10px_30px_rgb(37,99,235,0.5)] transition-transform duration-300 group-hover:scale-110">
                <span className="absolute inset-0 rounded-full bg-blue-500/50 animate-ping" aria-hidden="true" />
                <Play size={30} fill="currentColor" className="relative ml-1" />
              </span>
            </span>
          </button>
        )}
      </div>
      <div className="p-7">
        <p className="text-blue-400 text-xs font-bold uppercase tracking-[0.2em] mb-2">Watch · 50 seconds</p>
        <p className="font-display text-xl sm:text-2xl font-bold text-white leading-snug">Bryan walks you through his app</p>
        <p className="mt-3 text-sm font-semibold text-slate-400">Designed to get you fit from the comfort of your home</p>
      </div>
    </div>
  );
};

export const WorkoutPreviews = () => (
  <section id="workouts" data-testid="workouts-section" className="py-24 md:py-32 bg-slate-900 scroll-mt-20">
    <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
      <SectionHeading label="Inside the app" title="Taste a real follow-along workout" sub="This is the actual app — Bryan coaching you rep by rep, from his home to yours. No gym mirrors, no machines, no judgment." />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
        <Reveal>
          <VideoCard />
        </Reveal>
        <Reveal delay={0.1}>
          <div className="h-full bg-slate-950 rounded-[2rem] border border-slate-800 shadow-[0_8px_30px_rgb(0,0,0,0.3)] overflow-hidden flex flex-col">
            <div className="relative">
              <img data-testid="app-collections-image" src={IMAGES.appList} alt="Bryan Lee app workout collections" className="w-full aspect-[4/3] object-cover object-top" loading="lazy" />
              <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-slate-950 to-transparent" aria-hidden="true" />
            </div>
            <div className="p-8 pt-2 flex-1 flex flex-col justify-center">
              <h3 className="font-display text-2xl font-bold text-white mb-6">Your whole week, planned for you</h3>
              <ul className="space-y-5">
                {APP_POINTS.map((p, i) => (
                  <li key={i} data-testid={`app-point-${i}`} className="flex items-start gap-4">
                    <span className="w-10 h-10 rounded-xl bg-blue-500/15 text-blue-400 flex items-center justify-center shrink-0"><p.icon size={19} /></span>
                    <p className="text-sm sm:text-base text-slate-300 font-medium leading-relaxed pt-2">{p.text}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Reveal>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {IMAGES.workouts.map((w, i) => (
          <Reveal key={w.title} delay={0.08 * i}>
            <a data-testid={`workout-card-${i}`} href={CHECKOUT_URL} target="_blank" rel="noopener noreferrer" className="group block bg-slate-950 rounded-[2rem] overflow-hidden border border-slate-800 shadow-[0_8px_30px_rgb(0,0,0,0.25)] transition-[transform,box-shadow] duration-300 hover:-translate-y-2 hover:shadow-[0_24px_48px_rgb(0,0,0,0.4)] focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:outline-none focus-visible:ring-offset-2">
              <div className="relative overflow-hidden">
                <img src={w.img} alt={w.title} className="w-full aspect-[4/3] object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                <div className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/30 transition-colors duration-300 flex items-center justify-center">
                  <Play size={52} className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" fill="currentColor" />
                </div>
              </div>
              <div className="p-7">
                <h3 className="font-display text-xl font-bold text-white mb-4">{w.title}</h3>
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-500/10 text-blue-300 text-xs font-bold px-3 py-1.5"><Clock size={13} /> {w.time}</span>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-orange-500/10 text-orange-300 text-xs font-bold px-3 py-1.5"><Signal size={13} /> {w.level}</span>
                </div>
              </div>
            </a>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.2} className="mt-12 text-center">
        <p className="text-sm sm:text-base text-slate-500 font-medium">300+ workouts inside — new ones added every week</p>
      </Reveal>
    </div>
  </section>
);
